import { useCallback, useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import {
    stopAutoScroll,
    updateAutoScroll,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    EMPTY_DROP_TARGET,
    writeEditorDragData,
} from './utilities/dragAndDrop.js';

/**
 * Shared drag-and-drop state/handlers for blog item containers.
 * Keeps FLIP animation and auto-scroll behavior consistent across components.
 */
export const useDragContainer = ({
    items,
    edit = true,
    animationDependency = null,
}) => {

    const [dragEnabled, setDragEnabled] = useState(true);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dropTarget, setDropTarget] = useState(EMPTY_DROP_TARGET);

    const itemRefs = useRef({});
    const previousPositionsRef = useRef(null);
    const autoScrollFrameRef = useRef(null);
    const autoScrollDirectionRef = useRef(0);

    const itemKeys = useMemo(
        () => (Array.isArray(items) ? items.map((item, index) => item?.id || `editor-item-${index}`) : []),
        [items]
    );

    const enabled = edit && dragEnabled;
    const stopAutoScrollFn = useCallback(
        () => stopAutoScroll(autoScrollFrameRef, autoScrollDirectionRef),
        []
    );
    const updateAutoScrollFn = useCallback(
        (clientY) => updateAutoScroll(clientY, autoScrollFrameRef, autoScrollDirectionRef),
        []
    );

    useEffect(() => {
        return () => {
            stopAutoScrollFn();
        };
    }, [stopAutoScrollFn]);

    useEffect(() => {
        if (enabled) return;

        handleDragEnd({ stopAutoScrollFn, setDraggedIndex, setDropTarget });
    }, [enabled, stopAutoScrollFn]);

    useLayoutEffect(() => {
        if (!edit) {
            return;
        }

        const previousPositions = previousPositionsRef.current;
        if (!previousPositions) {
            return;
        }

        itemKeys.forEach((itemKey) => {
            const element = itemRefs.current[itemKey];
            const previousTop = previousPositions[itemKey];
            if (!element || previousTop === undefined) {
                return;
            }

            const currentTop = element.getBoundingClientRect().top;
            const deltaY = previousTop - currentTop;

            if (deltaY === 0) {
                return;
            }

            element.style.setProperty('--flip-offset-y', `${deltaY}px`);
            element.classList.add('flip-prep');

            requestAnimationFrame(() => {
                element.classList.add('flip-play');
                element.classList.remove('flip-prep');
            });

            const cleanup = () => {
                element.classList.remove('flip-play');
                element.classList.remove('flip-prep');
                element.style.removeProperty('--flip-offset-y');
                element.removeEventListener('transitionend', cleanup);
            };

            element.addEventListener('transitionend', cleanup);
        });

        previousPositionsRef.current = null;
    }, [animationDependency, edit, itemKeys]);

    const captureItemPositions = useCallback(() => {
        if (!enabled) {
            previousPositionsRef.current = null;
            return;
        }

        const positions = {};

        itemKeys.forEach((itemKey) => {
            const element = itemRefs.current[itemKey];
            if (element) {
                positions[itemKey] = element.getBoundingClientRect().top;
            }
        });

        previousPositionsRef.current = positions;
    }, [enabled, itemKeys]);

    const onDragStart = useCallback((event, index, nodeId = '') => {
        if (!enabled) {
            event?.preventDefault();
            return;
        }

        const started = writeEditorDragData(event?.dataTransfer, { nodeId, text: index, root: true });
        if (!started) {
            event?.preventDefault();
            return;
        }

        setDraggedIndex(index);
    }, [enabled]);

    const onDragOver = useCallback((event, targetIndex) => {
        if (!enabled) return;

        handleDragOver({
            event,
            targetIndex,
            draggedIndex,
            setDropTarget,
            updateAutoScrollFn,
            stopAutoScrollFn,
        });
    }, [draggedIndex, enabled, stopAutoScrollFn, updateAutoScrollFn]);

    const onDrop = useCallback((event, targetIndex, moveItem, beforePosition = 'above') => {
        if (!enabled) return;

        handleDrop({
            event,
            targetIndex,
            draggedIndex,
            dropTarget,
            moveItem,
            setDraggedIndex,
            setDropTarget,
            stopAutoScrollFn,
            beforePosition,
        });
    }, [draggedIndex, dropTarget, enabled, stopAutoScrollFn]);

    const onDragEnd = useCallback(() => {
        handleDragEnd({
            stopAutoScrollFn,
            setDraggedIndex,
            setDropTarget,
        });
    }, [stopAutoScrollFn]);

    const getDragClassName = useCallback((index) => {
        const showDropAbove = dropTarget.index === index && dropTarget.position === 'above';
        const showDropBelow = dropTarget.index === index && dropTarget.position === 'below';
        const showDropLeft = dropTarget.index === index && dropTarget.position === 'left';
        const showDropRight = dropTarget.index === index && dropTarget.position === 'right';
        const isDragging = draggedIndex === index;

        return [
            isDragging && enabled ? 'dragging' : '',
            showDropAbove && enabled ? 'drop-above' : '',
            showDropBelow && enabled ? 'drop-below' : '',
            showDropLeft && enabled ? 'drop-left' : '',
            showDropRight && enabled ? 'drop-right' : '',
        ].join(' ').trim();
    }, [draggedIndex, dropTarget.index, dropTarget.position, enabled]);

    const setItemRef = useCallback((itemKey, element) => {
        if (element) {
            itemRefs.current[itemKey] = element;
            return;
        }

        delete itemRefs.current[itemKey];
    }, []);

    return {
        dragEnabled,
        draggedIndex,
        dropTarget,
        itemKeys,
        setDragEnabled,
        captureItemPositions,
        onDragStart,
        onDragOver,
        onDrop,
        onDragEnd,
        getDragClassName,
        setItemRef,
    };
};
